import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import WrapperPage from "../../components/WrapperPage";
import CardView from "./components/CardView";
import BlankFormHead from "./components/BlankFormHead";
import TextAnswer from "./components/TextAnswer";
import RatingAnswer from "./components/RatingAnswer";
import DropDownAnswer from "./components/DropDownAnswer";
import BaseButton from "../../components/BaseButton";
import useModalActions from "../../hooks/useModalActions";
import SuccessfulySavedQuestFormModal from "../../modals/SuccessfulySavedQuestFormModal";
import useController from "./controller";
import useViewController from "./viewController";
import InsightInvitePartnerSentModal from "../../modals/InsightInvitePartnerSent";
import InsightAnswerPartnerSentModal from "../../modals/InsightAnswerPartnerSent";
import InsightFormProtect from "../../hocs/InsightFormProtect";
import { MODAL_TYPES } from "../../utils/emitter";
import ROUTES from "../../utils/routes.js";
import "./style.scss";
import useCurrentGame from "../../hooks/useCurrentGame";
import config from "../../config";

// Тема пустой анкеты (Figma 11691-46635): блок в рамке как у вопросов, текст
// по центру, инпут предзаполнен введённой темой и редактируем
function BlankTopicBlock({ value, onChange, hint, placeholder }) {
  BlankTopicBlock.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    hint: PropTypes.string,
    placeholder: PropTypes.string,
  };
  return (
    <div className="insight-questionnaire-form-page__blank-topic">
      <div className="insight-questionnaire-form-page__blank-topic-inner">
        <div className="insight-questionnaire-form-page__blank-topic-hint">
          {hint}
        </div>
        <input
          className="insight-questionnaire-form-page__blank-topic-input"
          type="text"
          maxLength={60}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </div>
  );
}

export default InsightFormProtect(function InsightQuestionnaireFormPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { openModal } = useModalActions();
  const location = useLocation();
  const { getCurrentGame } = useCurrentGame();
  const {
    questions,
    onSetQuestions,
    questionInfo,
    questionName,
    isFormValid,
    blankTitle,
    setBlankTitle,
    ...params
  } = useViewController();
  const { sendFormAnswer, saveDraft } = useController(
    {
      ...params,
      questions,
      isFormValid,
      onSetQuestions,
      questionInfo,
      blankTitle,
    },
    ROUTES
  );

  return (
    <div className="insight-questionnaire-form-page centering-page">
      <InsightInvitePartnerSentModal
        onClose={() => {
          if (location.state.prevPath === ROUTES.IN_GAME_PAGE) {
            navigate(ROUTES.IN_GAME_PAGE);
            return;
          }
          navigate(ROUTES.INSIGHT_QUESTIONNAIRE_PAGE);
        }}
      />
      <InsightAnswerPartnerSentModal
        onClose={() => {
          if (location.state.prevPath === ROUTES.IN_GAME_PAGE) {
            navigate(ROUTES.IN_GAME_PAGE);
            return;
          }
          navigate(ROUTES.INSIGHT_QUESTIONNAIRE_PAGE);
        }}
      />
      <SuccessfulySavedQuestFormModal
        onClose={() => {
          if (location.state.from === ROUTES.IN_GAME_PAGE) {
            navigate(ROUTES.IN_GAME_PAGE);
            return;
          }
          navigate(ROUTES.INSIGHT_QUESTIONNAIRE_PAGE);
        }}
      />
      <WrapperPage backBtn burgerMenu>
        {questionInfo?.formType === "BLANK" ? (
          // пустая анкета (Figma 11691-46635): карточка с бабочкой над вопросами,
          // название — в стиле титла обычной карточки
          <BlankFormHead name={questionName} />
        ) : questionInfo?.question ? (
          // рендерим карточку только после загрузки questionInfo — иначе на
          // BLANK-анкете успевал мигнуть CardView c src=/static-api/undefined (404)
          <CardView
            imgUrl={`${config.staticApiUrl}${questionInfo.question.cardUrl}`}
            title={questionName}
          />
        ) : null}

        <div className="insight-questionnaire-form-page__questions">
          {questionInfo?.formType === "BLANK" && questionInfo?.isCreated && (
            // Figma 11691-46635: тема анкеты — первым инпут-блоком формы;
            // предзаполнена введённым названием, правка обновляет заголовок
            // карточки live и customTitle обеих анкет пары на отправке.
            // items 17/20b: редактируемое название темы видит ТОЛЬКО автор
            // анкеты (isCreated). Второй партнёр (получатель) сразу видит уже
            // заданное название карточки и идёт к вопросам 1,2,3 — без этой рамки.
            <BlankTopicBlock
              value={blankTitle}
              onChange={setBlankTitle}
              hint={t("insight_questionnaire_form_page.blank_topic_hint")}
              placeholder={t("insight_questionnaire_form_page.myAnswer")}
            />
          )}
          {questions.map((question) => {
            if (question.type === "text") {
              return (
                <TextAnswer
                  onChange={onSetQuestions}
                  value={question.value}
                  title={question.title}
                  key={question.number}
                  number={question.number}
                  name={`question-${question.number}`}
                />
              );
            }
            if (question.type === "rating") {
              return (
                <RatingAnswer
                  value={question.value}
                  onChange={onSetQuestions}
                  title={question.title}
                  key={question.number}
                  number={question.number}
                  name={`question-${question.number}`}
                />
              );
            }
            if (question.type === "dropdown") {
              return (
                <DropDownAnswer
                  value={question.value}
                  onChange={onSetQuestions}
                  title={question.title}
                  key={question.number}
                  number={question.number}
                  name={`question-${question.number}`}
                />
              );
            }
            return null;
          })}
        </div>

        <div className="insight-questionnaire-form-page__action-buttons">
          <BaseButton
            disabled={!isFormValid || !getCurrentGame().partner}
            onClick={sendFormAnswer}
          >
            {t("insight_questionnaire_form_page.send")}
          </BaseButton>
          <BaseButton
            unfilledGray
            onClick={() => {
              saveDraft();
              openModal(MODAL_TYPES.SUCCESSFULY_SAVED_QUEST_FORM_MODAL);
            }}
          >
            {t("insight_questionnaire_form_page.save_draft")}
          </BaseButton>
        </div>
      </WrapperPage>
    </div>
  );
});
